// Field save/create helpers (PR #29 rework). Pure functions so they can be
// unit-tested; App.tsx wires them to the UI.

export function newClientRequestId(kind: string): string {
  const c: any = typeof crypto !== 'undefined' ? crypto : undefined;
  const rnd = c?.randomUUID ? c.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${kind}-${rnd}`;
}

// ---- server restart detection: X-UGP-Boot-Id is on every response.
let lastBootId: string | null = null;
const listeners: Array<(prev: string, next: string) => void> = [];

export function noteBootId(headers: any): void {
  const id = headers?.['x-ugp-boot-id'] ?? headers?.get?.('x-ugp-boot-id');
  if (!id) return;
  const prev = lastBootId;
  lastBootId = String(id);
  if (prev && prev !== lastBootId) listeners.forEach((fn) => fn(prev, lastBootId as string));
}

export function currentBootId(): string | null {
  return lastBootId;
}

export function onServerRestart(fn: (prev: string, next: string) => void): () => void {
  listeners.push(fn);
  return () => {
    const i = listeners.indexOf(fn);
    if (i >= 0) listeners.splice(i, 1);
  };
}

export function _resetBootIdForTests(): void {
  lastBootId = null;
  listeners.splice(0, listeners.length);
}

export type RestoredHouse = { survey_id: string; from_session?: string; added_by?: string };

export function restoredText(restored: RestoredHouse[] = []): string {
  if (!restored.length) return '';
  const other = restored.filter((r) => r.from_session === 'another session');
  const ids = restored.map((r) => r.survey_id).join(', ');
  return (
    `${restored.length} house(s) added but not yet saved are on your map: ${ids}.` +
    (other.length ? ` ${other.length} came from another session or device.` : '') +
    ' They are not saved until someone presses Save.'
  );
}

/** Banner after a restart. Only mentions restored houses that were really
 *  restored (the server lists them); otherwise says nothing was restored. */
export function restartBannerText(restored: RestoredHouse[] = []): string {
  const r = restored.length ? ` ${restoredText(restored)}` : ' No unsaved houses needed restoring.';
  return `The server restarted since your last request.${r} Check the map before adding anything again.`;
}

const restoredListeners: Array<(r: RestoredHouse[]) => void> = [];
export function onRestoredUnsaved(fn: (r: RestoredHouse[]) => void): () => void {
  restoredListeners.push(fn);
  return () => { const i = restoredListeners.indexOf(fn); if (i >= 0) restoredListeners.splice(i, 1); };
}
export function notifyRestoredUnsaved(restored: any): void {
  if (Array.isArray(restored) && restored.length) restoredListeners.forEach((fn) => fn(restored));
}

// ---- messages
export function saveConfirmationText(result: any): string {
  if (result?.nothing_to_save) {
    return (
      `Saved, but the server had NO unsaved changes for this session (version ${result?.version}).\n\n` +
      'If you just added houses and they are not on the map, reload the project and check before entering them again.'
    );
  }
  const parts = [`SAVED: version ${result?.version}.`];
  const created: string[] = Array.isArray(result?.connections_created) ? result.connections_created : [];
  if (created.length) parts.push(`New connections stored: ${created.length} (${created.slice(0, 10).join(', ')}${created.length > 10 ? ', …' : ''}).`);
  if (result?.connections_modified) parts.push(`Connections changed: ${result.connections_modified}.`);
  if (result?.connections_deleted) parts.push(`Connections deleted: ${result.connections_deleted}.`);
  if (typeof result?.connections_total === 'number') parts.push(`Connections in project: ${result.connections_total}.`);
  if (result?.idempotent_replay) parts.push('(Retry: this save had already completed; nothing was saved twice.)');
  return parts.join('\n');
}

/** Honest NOT SAVED text: only claim the edits are still there when the
 *  server answered, it has not restarted since the session started and it
 *  reports pending changes. */
export function notSavedText(error: any, bootAtSessionStart: string | null): string {
  const status = error?.response?.status;
  const d = error?.response?.data?.detail;
  const why = typeof d === 'string' ? d : (d?.message || error?.message || 'unknown error');
  const bootNow = currentBootId();
  const restarted = !!(bootAtSessionStart && bootNow && bootAtSessionStart !== bootNow);
  if (status === 404 || restarted) {
    return `NOT SAVED: ${why}\n\nThe server restarted or lost this session. Unsaved houses are kept in the server journal and restored when the project reopens; reload the project and check the map before entering anything again.`;
  }
  if (!status) {
    return `NOT SAVED: no reply from the server (${why}).\n\nWe cannot tell whether your edits are still on the server. Do not re-enter houses: retry Save, or reload and check the map.`;
  }
  return `NOT SAVED: ${why}\n\nThe server is up and still holds your unsaved edits in this session; do not re-enter them. Try Save again.`;
}

export const MAX_SAVE_RETRIES = 3;
